import React from 'react';
import MaterialTable, { MaterialTableProps, Query, QueryResult, Components, Column } from 'material-table';
import {
    Grid2 as Grid,
    Icon,
    IconButton,
    Tooltip,
    Typography,
    Menu,
    MenuItem,
    Select,
    SelectChangeEvent,
    ListItemText,
    Input,
    InputProps,
    IconButtonProps,
} from '@mui/material';
import * as _ from 'lodash';
import {
    FirstPage,
    KeyboardArrowLeft,
    KeyboardArrowRight,
    LastPage,
    SaveAlt,
} from '@mui/icons-material';
import { CsvBuilder } from "filefy";
import "jspdf-autotable";
import * as FileSaver from 'file-saver';
import { Loading, SearchTextField } from './';
import { styled } from '@mui/styles';
import { fontFamily, styleComponents, truncateNumber } from '../../utils';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { CSSObject } from '@mui/system';
import { Theme } from '@mui/material/styles/createTheme';
import { Components as StyleComponents } from '@mui/material/styles/components';

// tslint:disable-next-line:no-var-requires
const jsPDF = typeof window !== "undefined" ? require("jspdf").jsPDF : null;

interface Props extends MaterialTableProps<any> {
    readonly styled: StyledProps;
    readonly showBox?: boolean;
    readonly refreshSeconds?: number;
    readonly onRefresh?: (oldData: any, newData: any) => void;
    readonly style?: React.CSSProperties;
    readonly cellStyle?: CSSObject;
    readonly ignoreSmall?: boolean;
    readonly ignoreSearchReadOnly?: boolean;
    readonly backgroundColor?: 'primary' | 'secondary';
}

interface State {
    readonly openExportMenu: boolean;
}

export class Table extends React.Component<Props, State> {
    state = {
        openExportMenu: false,
    };

    private tableRef: any;
    private timer: NodeJS.Timeout | null = null;

    componentDidMount() {
        const { refreshSeconds } = this.props;
        if (refreshSeconds) {
            this.timer = setInterval(this.onRefresh, refreshSeconds * 1000);
        }
    }

    componentWillUnmount() {
        if (this.timer) {
            clearInterval(this.timer);
        }
    }

    render() {
        const { styled, ignoreSmall } = this.props;
        this.tableRef = this.props.tableRef ? this.props.tableRef : React.createRef();
        if (ignoreSmall) {
            return this.renderTableRegular(styled);
        }
        return (
            <>
                {!this.props.styled.smallUp && this.renderTableSmall(styled)}
                {this.props.styled.smallUp && this.renderTableRegular(styled)}
            </>
        );
    }

    private getTheme(styled: StyledProps, large: boolean = true): Theme {
        const { cellStyle } = this.props;
        const { themeMode } = styled;

        const tableComponents: StyleComponents<Omit<Theme, 'components'>> = {
            ...styleComponents(themeMode),
            MuiPaper: {
                styleOverrides: {
                    root: {
                        padding: '5px 0px',
                        borderRadius: 8,
                        boxShadow: 'none',
                        backgroundColor: styled.themeMode === 'light' ? '#fff' : '#212121',
                    }
                }
            },
            MuiTable: {
                styleOverrides: {
                    root: {
                        fontWeight: 400,
                        fontSize: 12,
                        fontFamily,
                        color: themeMode === 'light' ? '#404040' : '#ffffff',
                    },
                },
            },
            MuiTableBody: {
                styleOverrides: {
                    root: {
                        backgroundColor: 'transparent',
                    }
                },
            },
            MuiTableRow: {
                styleOverrides: {
                    root: {
                        '&.MuiTableRow-hover:hover': {
                            backgroundColor: themeMode === 'light' ? '#e6e6e6 !important' : '#3f3f3f !important',
                        }
                    },
                    selected: {
                        backgroundColor: themeMode === 'light' ? '#e6e6e6 !important' : '#3f3f3f !important',
                        borderTop: themeMode === 'light' ? `1px solid #e9ebee` : '1px solid #323337',
                        borderBottom: themeMode === 'light' ? `1px solid #e9ebee` : '1px solid #323337',
                    },
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: large ? {
                            padding: 5,
                            textAlign: 'left',
                            fontWeight: 400,
                            fontSize: 12,
                            fontFamily,
                            lineHeight: 'inherit',
                            color: themeMode === 'light' ? 'rgba(0, 0, 0, 0.87)' : '#ffffffe6',
                            borderBottom: themeMode === 'light' ? '1px solid #e9ebee' : '1px solid #323337',
                            ...cellStyle,
                        }
                        : {
                            padding: 16,
                            textAlign: 'center',
                            fontWeight: 400,
                            fontSize: 12,
                            fontFamily,
                            lineHeight: 'inherit',
                            color: themeMode === 'light' ? '#404040' : '#adadad',
                            borderBottom: themeMode === 'light' ? '1px solid #e9ebee' : '1px solid #323337',
                            ...cellStyle,
                        },
                    footer: {
                        borderTop: themeMode === 'light' ? `1px solid #e9ebee` : '1px solid #323337',
                        borderBottom: themeMode === 'light' ? `1px solid #e9ebee` : '1px solid #323337',
                        color: themeMode === 'light' ? '#404040' : '#adadad',
                        border: 'none',
                        borderRadius: 0,
                    },
                    paddingNone: {
                        color: themeMode === 'light' ? '#404040' : '#adadad',
                        padding: '0px !important',
                    },
                },
            },
            MuiTablePagination: {
                styleOverrides: {
                    root: {
                        fontSize: 10,
                        fontWeight: 400,
                    },
                    select: {
                        fontSize: 10,
                        fontWeight: 400,
                    },
                    toolbar: {
                        display: 'flex',
                        fontSize: 10,
                        fontWeight: 400,
                    },
                    displayedRows: {
                        fontSize: 10,
                        fontWeight: 400,
                    },
                    selectLabel: {
                        fontSize: 10,
                        fontWeight: 400,
                    },
                },
            },
            MuiCheckbox: {
                styleOverrides: {
                    root: {
                        color: 'inherit',
                        width: 20,
                        height: 20,
                    },
                    colorSecondary: {
                        '&$checked': {
                            color: '#0073bb',
                        },
                    }
                },
            },
            MuiSvgIcon: {
                styleOverrides: {
                    root: {
                        fontSize: '1rem',
                    },
                },
            },
            MuiIcon: {
                styleOverrides: {
                    root: {
                        fontSize: '1.2rem',
                    }
                }
            },
            MuiInputBase: {
                styleOverrides: {
                    root: {
                    },
                    multiline: {
                    },
                    input: {
                    }
                }
            },
        };
        return createTheme({ components: tableComponents });
    }

    private renderTableSmall(styled: StyledProps) {
        const { backgroundColor, style, columns, data, options, actions } = this.props;
        let { showBox } = this.props;
        const { themeMode } = styled;
        showBox = showBox !== undefined ? showBox : true;
        const newData: any[] = [];

        if (typeof data !== "function" ) {
            data.forEach(d => {
                newData.push(
                    {
                        ...d,
                        newValue: (this.renderSmallTableData(styled, d, columns, actions)),
                    }
                );
            });
        }

        const tableComponents: Components = {
            ...this.renderComponents(styled),
            Header: props => <></>,
        };

        const newColumns: any[] = columns.map(c => ({ ...c, hidden: true, searchable: true}));
        newColumns.push({ title: 'Data', field: 'newValue', sorting: false, searchable: false });

        const getData = (query: any) => this.getSmallTableData(styled, query, columns, actions);

        const toShowActions = actions ? actions.filter(action => {
            if (typeof action === "function") {
                return false;
            }
            return action.position === 'toolbarOnSelect' || action.position === 'toolbar';
        }) : undefined;

        return (
            <ThemeProvider theme={this.getTheme(styled, false)}>
                <div
                    style={{
                        boxShadow: !backgroundColor || backgroundColor === 'primary' ? ( showBox ? themeMode === 'light' ? 'rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px'
                            : 'rgba(0, 0, 0, 0.3) 0px 1px 2px 0px, rgba(0, 0, 0, 0.15) 0px 1px 3px 1px' : 'none') : 'none',
                        borderRadius: !backgroundColor || backgroundColor === 'primary' ? 8 : 0,
                        boxSizing: showBox ? 'border-box' : undefined,
                        backgroundColor: !backgroundColor || backgroundColor === 'primary' ? ( themeMode === 'light' ? '#fff' : '#1b2025' ) :
                            ( themeMode === 'light' ? '#f5f5f5' : '#3f3f3f' ),
                        padding: !backgroundColor || backgroundColor === 'primary' ? 2 : 0,
                        ...style,
                    }}
                >
                    <MaterialTable
                        {...this.props}
                        tableRef={this.tableRef}
                        columns={newColumns}
                        data={ typeof data !== "function" ? newData : getData}
                        components = {tableComponents}
                        actions={toShowActions}
                        options={{
                            ...options,
                            overflowY: 'hidden',
                        }}
                    />
                </div>
            </ThemeProvider>
        );
    }

    private renderTableRegular(styled: StyledProps) {
        const { style, options, backgroundColor } = this.props;
        let { showBox } = this.props;
        const { themeMode } = styled;
        showBox = showBox !== undefined ? showBox : true;
        const tableComponents: Components = this.renderComponents(styled);

        const headerStyle = this.getHeaderStyle(styled);

        return (
            <ThemeProvider theme={this.getTheme(styled)}>
                <div
                    style={{
                        boxShadow: !backgroundColor || backgroundColor === 'primary' ? ( showBox ? themeMode === 'light' ? 'rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px'
                            : 'rgba(0, 0, 0, 0.3) 0px 1px 2px 0px, rgba(0, 0, 0, 0.15) 0px 1px 3px 1px' : 'none') : 'none',
                        borderRadius: !backgroundColor || backgroundColor === 'primary' ? 8 : 0,
                        boxSizing: showBox ? 'border-box' : undefined,
                        backgroundColor: !backgroundColor || backgroundColor === 'primary' ? ( themeMode === 'light' ? '#fff' : '#1b2025' ) :
                            ( themeMode === 'light' ? '#f5f5f5' : '#3f3f3f' ),
                        padding: !backgroundColor || backgroundColor === 'primary' ? 2 : 0,
                        ...style,
                    }}
                >
                    <MaterialTable
                        {...this.props}
                        tableRef={this.tableRef}
                        components = {tableComponents}
                        options={{
                            ...options,
                            headerStyle,
                            overflowY: 'hidden',
                        }}
                    />
                </div>
            </ThemeProvider>
        );
    }

    private getHeaderStyle(styled: StyledProps): React.CSSProperties {
        const { options } = this.props;
        const selection = options && options.selection;
        const headerStyle = options && options.headerStyle ? options.headerStyle : {};
        return {
            height: 25,
            padding: selection ? '0px 5px' : '4px 5px',
            color: styled.themeMode === 'light' ? '#000000b3' : '#ffffffa3',
            fontSize: '0.75rem',
            fontWeight: 600,
            lineHeight: '1.3',
            backgroundColor: 'transparent',
            ...headerStyle,
        };
    }

    private renderComponents(styled: StyledProps): Components {
        return {
            Toolbar: props => this.renderComponentToolbar(styled, props),
            Actions: props => this.renderComponentActions(styled, props),
            Action: props => this.renderComponentAction(props),
            OverlayLoading: props => this.renderComponentLoading(styled),
            Pagination: props => this.renderComponentPagination(styled, props),
            ...this.props.components
        };
    }

    private renderComponentActions(styled: StyledProps, props: any) {
        if (!props.actions) {
            return <></>;
        }
        return (
            <div style={{
                width: '100%',
                display: 'grid',
                gridAutoFlow: 'column',
                justifyContent: 'center',
            }}>
                {_.map(props.actions, action => this.renderDataAction(styled, props.data, action))}
            </div>
        );
    }

    private renderComponentAction(props: any) {
        return (
            <Tooltip title={props.tooltip}>
                <IconButton component="label" disabled={props.disabled} onClick={props.onClick}>
                    <props.icon style={{ width: 20 , height: 20, margin: '0px 10px'}}/>
                </IconButton>
            </Tooltip>
        );
    }

    private renderComponentLoading(styled: StyledProps) {
        const { backgroundColor } = this.props;
        return (
            <div
                style={{
                    display: "table",
                    width: "100%",
                    height: "100%",
                    padding: 20,
                    backgroundColor: !backgroundColor || backgroundColor === 'primary' ? ( styled.themeMode === 'light' ? '#fff' : '#212121' ) :
                        ( styled.themeMode === 'light' ? '#f5f5f5' : '#3f3f3f' ),
                    opacity: 0.9,
                }}
            >
                <div
                    style={{
                        display: "table-cell",
                        width: "100%",
                        height: "100%",
                        verticalAlign: "middle",
                        textAlign: "center",
                    }}
                >
                    <Loading styled={styled}/>
                </div>
            </div>
        );
    }

    private renderComponentPagination(styled: StyledProps, props: any) {
        const {
            count,
            page,
            rowsPerPage,
            rowsPerPageOptions,
        } = props;

        const handleFirstPageButtonClick = (event: any) => {
            props.onPageChange(event, 0);
        };

        const handleBackButtonClick = (event: any) => {
            props.onPageChange(event, page - 1);
        };

        const handleNextButtonClick = (event: any) => {
            props.onPageChange(event, page + 1);
        };

        const handleLastPageButtonClick = (event: any) => {
            props.onPageChange(
                event,
                Math.max(0, Math.ceil(props.count / rowsPerPage) - 1)
            );
        };

        const onChangePerPageOptions = async (event: SelectChangeEvent<unknown>) => {
            props.onRowsPerPageChange(event);
        };

        const from = count === 0 ? 0 : page * rowsPerPage + 1;
        const to = Math.min((page + 1) * rowsPerPage, count);
        const renderValue = (value: any) => (`${value} rows`);

        if (count === 0) {
            return <></>;
        }

        return (
            <div style={{
                    display: 'grid',
                    justifyContent: 'flex-end',
                    gridAutoFlow: 'column',
                    columnGap: '15px',
                    padding: '0px 10px',
                    backgroundColor: 'transparent',
                    height: 32,
                    alignItems: 'center'
                }}
            >
                <StyledSelect
                    variant={'standard'}
                    value={rowsPerPage}
                    onChange={onChangePerPageOptions}
                    native={false}
                    input={<StyledInput styled={styled} />}
                    style={{
                        width: '100%',
                        height: 30,
                    }}
                    renderValue={renderValue}
                >
                    {rowsPerPageOptions.map((option: any) => (
                        <MenuItem key={option} value={option}>
                            <ListItemText primary={option} />
                        </MenuItem>
                    ))}
                </StyledSelect>
                {page > 0 && (
                    <>
                        <Tooltip title={'First Page'}>
                            <StyledIconButton
                                styled={styled}
                                onClick={handleFirstPageButtonClick}
                                disabled={page === 0}
                                size={'medium'}
                                style={{ padding: '8px 0px', height: 30 }}
                            >
                                <FirstPage style={{ height: 20, width: 20 }}/>
                            </StyledIconButton>
                        </Tooltip>
                        <Tooltip title={'Previous Page'}>
                            <StyledIconButton
                                styled={styled}
                                onClick={handleBackButtonClick}
                                disabled={page === 0}
                                size={'medium'}
                                style={{ padding: '8px 0px', height: 30 }}
                            >
                                <KeyboardArrowLeft style={{ height: 20, width: 20 }}/>
                            </StyledIconButton>
                        </Tooltip>
                    </>
                )}
                <Typography
                    variant="caption"
                    style={{
                        flex: 1,
                        textAlign: "center",
                        alignSelf: "center",
                        flexBasis: "inherit",
                        fontSize: '10px',
                        fontWeight: 500,
                        color: styled.themeMode === 'light' ? '#0000008c' : '#ffffff99',
                    }}
                >
                    {`${from}-${to} of ${props.count.toLocaleString()}`}
                </Typography>
                {page < Math.ceil(count / rowsPerPage) - 1 && (
                    <>
                        <Tooltip title={'Next Page'}>
                            <StyledIconButton
                                styled={styled}
                                onClick={handleNextButtonClick}
                                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                                size={'medium'}
                                style={{ padding: '8px 0px', height: 30 }}
                            >
                                <KeyboardArrowRight style={{ height: 20, width: 20 }}/>
                            </StyledIconButton>
                        </Tooltip>
                        <Tooltip title={'Last Page'}>
                            <StyledIconButton
                                styled={styled}
                                onClick={handleLastPageButtonClick}
                                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                                size={'medium'}
                                style={{ padding: '8px 0px', height: 30 }}
                            >
                                <LastPage style={{ height: 20, width: 20 }}/>
                            </StyledIconButton>
                        </Tooltip>
                    </>
                )}
            </div>
        );
    }

    private renderComponentToolbar(styled: StyledProps, props: any) {
        const { title, search, exportButton, actions, showTitle } = props;
        const toShowActions = actions.filter((action: any) => action.position !== 'row' && action.position !== 'toolbarOnSelect');
        if (!showTitle && !search && !exportButton && toShowActions.length === 0) {
            return <></>;
        }


        return (
            <Grid
                container={true}
                spacing={1}
                style={{
                    width: '100%',
                    margin: 'auto',
                    padding: '5px 8px 10px',
                    backgroundColor: 'transparent',
                    borderRadius: 0,
                }}
            >
                {showTitle &&
                    <Grid size={12}>
                        {_.isString(title) && (
                            <Typography variant="subtitle1" style={{fontWeight: 'bold', fontSize: '1rem'}}>
                                {title}
                            </Typography>
                        )}
                        {!_.isString(title) && title}
                    </Grid>
                }
                {search &&
                    <Grid size={{ xs:12, sm: 7 }} style={{ margin: 'auto 0px' }}>
                        {this.renderSearch(styled, props)}
                    </Grid>
                }
                {(toShowActions.length > 0 || exportButton) &&
                    <Grid size={{ xs:12, sm: search ? 5 : 12 }} style={{ margin: 'auto' }}>
                        <Grid container={true} justifyContent="flex-end" alignItems="flex-end" spacing={0}>
                            <Grid size={{ xs:12 }} justifyContent="flex-end" alignItems="flex-end" style={{ display: 'flex' }}>
                                {_.map(toShowActions, action => {
                                    if (typeof action === "function") {
                                        return <></>;
                                    }
                                    if (action.hidden) {
                                        return <></>;
                                    }
                                    if (action.position === "toolbarOnSelect" && (!props.selectedRows || props.selectedRows.length === 0)) {
                                        return <></>;
                                    }
                                    const handleOnClickAction = (event: any) => {
                                        if (action.onClick) {
                                            action.onClick(event, action.position === "toolbarOnSelect" ? props.selectedRows : props.data);
                                            event.stopPropagation();
                                        }
                                    };

                                    return (
                                        <Tooltip title={action.tooltip}>
                                            <IconButton color="secondary" component="label" disabled={action.disabled} onClick={handleOnClickAction} style={{ padding: '0 5px' }}>
                                                <action.icon style={{ width: 20 , height: 20, margin: '0px 10px'}}/>
                                            </IconButton>
                                        </Tooltip>
                                    );
                                })}
                                {props.exportButton && this.renderExportActions(props)}
                            </Grid>
                        </Grid>
                    </Grid>
                }
            </Grid>
        );
    }

    private renderSearch(styled: StyledProps, props: any) {
        const isRemoteData = !_.isArray(this.props.data);

        const onSearch = (searchText: string) => {
            props.dataManager.changeSearchText(searchText);
            props.onSearchChanged(searchText);
        };

        return (
            <SearchTextField
                styled={styled}
                searchText={props.searchText}
                isRemoteData={isRemoteData}
                onSearch={onSearch}
            />
        );
    }

    private renderExportActions(props: any) {
        if (this.props.data.length === 0) {
            return <></>;
        }

        let anchorEl: HTMLElement | undefined;
        const openMenu = (event: any) => {
            anchorEl = event.currentTarget;
            this.setState({ openExportMenu: true });
        };
        const closeMenu = () => this.setState({ openExportMenu: false });
        const exportCsv = () => {
            this.exportCsv(props);
            closeMenu();
        };
        const exportPdf = () => {
            this.exportPdf(props);
            closeMenu();
        };
        const exportJson = () => {
            this.exportJson(props);
            closeMenu();
        };

        const open = this.state.openExportMenu;

        return (
            <div>
                <Tooltip title={'Export'}>
                    <IconButton color="secondary" component="label" onClick={openMenu}>
                        <SaveAlt style={{ width: 20 , height: 20 }}/>
                    </IconButton>
                </Tooltip>
                <Menu
                    id={`menu-title-export`}
                    anchorEl={anchorEl}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    open={open}
                    onClose={closeMenu}
                >
                    <MenuItem color="inherit" value={0} onClick={exportCsv}>
                        {'Export Csv'}
                    </MenuItem>
                    <MenuItem color="inherit" value={1} onClick={exportPdf}>
                        {'Export Pdf'}
                    </MenuItem>
                    <MenuItem color="inherit" value={2} onClick={exportJson}>
                        {'Export Json'}
                    </MenuItem>
                </Menu>
            </div>
        );
    }

    private getTableData = (props: any) => {
        const columns = props.columns
            .filter(
                (columnDef: any) =>
                    (!columnDef.hidden || columnDef.export === true) &&
                    columnDef.export !== false &&
                    columnDef.field
            )
            .sort((a: any, b: any) =>
                a.tableData.columnOrder > b.tableData.columnOrder ? 1 : -1
            );
        const data = (props.exportAllData
                ? this.props.data
                : props.renderData
        ).map((rowData: any) =>
            columns.map((columnDef: any) => props.getFieldValue(rowData, columnDef))
        );

        return [columns, data];
    };

    private exportCsv = (props: any) => {
        const [ columns, data ] = this.getTableData(props);
        const fileName = this.getFileName(props);
        const builder = new CsvBuilder(fileName + ".csv");
        builder
            .setDelimeter(props.exportDelimiter)
            .setColumns(columns.map((columnDef: any) => columnDef.title))
            .addRows(data)
            .exportFile();
    };

    private exportPdf = (props: any) => {
        if (jsPDF !== null) {
            const [columns, data] = this.getTableData(props);

            const content = {
                startY: 50,
                head: [columns.map((columnDef: any) => columnDef.title)],
                body: data,
            };

            const unit = "pt";
            const size = "A4";
            const orientation = "landscape";

            const doc = new jsPDF(orientation, unit, size);
            doc.setFontSize(15);
            doc.text(props.exportFileName || this.props.title, 40, 40);
            doc.autoTable(content);
            doc.save(
                (props.exportFileName || this.props.title || "data") + ".pdf"
            );
        }
    };

    private exportJson = (props: any) => {
        const data = _.isArray(this.props.data) ? this.props.data : [];
        const fileName = this.getFileName(props);
        const file = new File([JSON.stringify(data, null, 2)], `${fileName}.json`, { type: 'text/plain;charset=utf-8' });
        FileSaver.saveAs(file);
    };

    private getFileName = (props: any) => {
        let fileName = this.props.title || "data";
        if (props.exportFileName) {
            fileName =
                typeof props.exportFileName === "function"
                    ? props.exportFileName()
                    : props.exportFileName;
        }
        return fileName;
    };

    private renderDataAction(styled: StyledProps, data: any, action: any, large: boolean = true) {
        if (typeof action === "function") {
            action = action(data);
            if (!action) {
                return <></>;
            }
        }

        if (action.action) {
            action = action.action(data);
            if (!action) {
                return <></>;
            }
        }

        if (action.hidden) {
            return <></>;
        }

        const disabled = action.disabled;

        const handleOnClick = (event: any) => {
            if (action.onClick) {
                action.onClick(event, data);
                event.stopPropagation();
            }
        };

        const icon =
            typeof action.icon === "string" ? (
                <Icon {...action.iconProps} style={{ width: large ? 20 : 25 , height: large ? 20 : 25 }}>{action.icon}</Icon>
            ) : typeof action.icon === "function" ? (
                action.icon({ ...action.iconProps, disabled })
            ) : (
                <action.icon style={{ width: large ? 20 : 25 , height: large ? 20 : 25 }}/>
            );

        const button = (
            <StyledIconButton
                styled={this.props.styled}
                color="inherit"
                disabled={disabled}
                onClick={handleOnClick}
                style={{
                    padding: 0,
                    margin: large ? '0px 5px' : '0px 10px',
                }}
            >
                {icon}
            </StyledIconButton>
        );

        if (action.tooltip) {
            return disabled ? (
                <Tooltip title={action.tooltip}>
                    <span>{button}</span>
                </Tooltip>
            ) : (
                <Tooltip title={action.tooltip}>{button}</Tooltip>
            );
        } else {
            return button;
        }
    }

    private renderSmallTableData(styled: StyledProps, data: any, columns: Column<any>[], actions?: any[]) {
        const rowActions = actions ? actions.filter(action => {
            if (typeof action === "function") {
                return true;
            }
            return action.position === 'row';
        }) : undefined;

        return (
            <Grid container={true} direction="row" justifyContent="center" alignItems="center" spacing={1}>
                {_.map(columns, (column, i) => {
                    const field = column.field;
                    if (typeof field === "string") {
                        return (
                            <>
                                <Grid size={12} style={{ display: 'flex', justifyContent: 'center' }}>
                                    <Typography variant={'subtitle2'} style={{ fontWeight: 600 }}>
                                        {column.title}
                                    </Typography>
                                </Grid>
                                <Grid size={12} style={{ display: 'flex', justifyContent: 'center' }}>
                                    {column.render ? column.render(data, "row") : data[field]}
                                </Grid>
                            </>
                        );
                    }
                    return <></>;
                })}
                {rowActions && rowActions.length > 0 && (
                    <>
                        <Grid size={{ xs:12 }} />
                        <Grid size={{ xs:12 }} />
                        <Grid size={12}>
                            {_.map(rowActions, action => this.renderDataAction(styled, data, action, false))}
                        </Grid>
                    </>
                )}
            </Grid>
        );
    }

    private getSmallTableData = async (styled: StyledProps, query: Query<any>, columns: Column<any>[], actions?: any[]) : Promise<QueryResult<any>> => {
        if (typeof this.props.data !== "function" ) {
            return {
                data: [],
                page: 0,
                totalCount: 0,
            };
        }

        const data = await this.props.data(query);
        const newData: any[] = [];
        data.data.forEach(d => {
            newData.push(
                {
                    newValue: (this.renderSmallTableData(styled, d, columns, actions)),
                    ...d,
                }
            );
        });
        data.data = newData;
        return data;
    };

    private onRefresh = async () => {
        if (!this.tableRef) {
            return;
        }

        const { data, onRefresh } = this.props;
        if (typeof data !== "function" || !onRefresh) {
            return;
        }

        const current = this.tableRef.current;
        if  (!current) {
            return;
        }

        const query = current.state.query;
        const dataManager = current.dataManager;
        const result = await data(query);
        if(!result || !result.data) {
            return;
        }

        onRefresh(dataManager.data, result.data);
    };
}

const StyledSelect = styled(Select)(({
    '& .MuiSelect-select': {
        paddingTop: 0,
        paddingBottom: 0,
        boxShadow: 'none',
        backgroundColor: 'transparent',
    },
    '$.MuiSelect-icon': {
        color: 'inherit',
    }
}));

interface StyledInputProps extends InputProps {
    styled: StyledProps;
}
const StyledInput = styled((props: StyledInputProps) => (
        <Input
            {...props}
            sx={{
                '&.MuiInput-root': {
                    fontSize: '10px',
                    fontWeight: 500,
                    color: props.styled.themeMode === 'light' ? '#0000008c' : '#ffffff99',
                    '&:after': {
                        borderBottom: 'none !important',
                        '&:hover': {
                            borderBottom: 'none !important',
                        },
                    },
                    '&:before': {
                        borderBottom: 'none !important',
                        '&:hover': {
                            borderBottom: 'none !important',
                        },
                    },
                    '&:hover': {
                        borderBottom: 'none !important',
                    },
                },
            }}
        />
))(() => ({}));

interface StyledIconButtonProps extends IconButtonProps {
    styled: StyledProps;
}
const StyledIconButton = styled((props: StyledIconButtonProps) => (
        <IconButton
            {...props}
            sx={{
                color: props.styled.themeMode === 'light' ? '#0073bb' : '#7cacf8',
                '&:disabled': {
                    color: props.styled.themeMode === 'light' ? 'rgba(0, 0, 0, 0.26)' : 'rgba(1, 1, 1, 0.26)',
                },
                '&:hover': {
                    backgroundColor: 'transparent',
                }
            }}
        />
))(() => ({}));
